 import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isAthlete, isGuardian, isPolaris, setTermsAccepted, setHasProfile } from "../utils/auth";
import { loginRoleFromSources, resolvePostLoginPath, takePostLoginPath } from "../utils/loginRedirect";
import { resolveGuardianPostLoginPath } from "../utils/viewedAthlete";
import { useAuth } from "../contexts/AuthContext";

function useQuery() {
    const { search } = useLocation();
    return new URLSearchParams(search);
}

export default function GoogleLoginCallback() {
    const q = useQuery();
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();

    useEffect(() => {
        // JWT token is now in HTTP-only cookie (SESSION_TOKEN)
        // We can't access it directly, but the browser will send it automatically

        const userId = q.get("userId");
        const hasProfile = q.get("hasProfile") === "true";
        const termsAccepted = q.get("termsAndConditionsAccepted") === "true";
        const hasLoggedInBefore = q.get("hasLoggedInBefore");
        const role = q.get("role") || q.get("userType");
        const profileType = q.get("profileType");
        const guardianAthletesParam = q.get("guardianAthletes");

        if (userId) {
            // Store only non-sensitive data in localStorage
            localStorage.setItem("loggedInUserId", userId);
            localStorage.setItem("userId", userId);
            localStorage.setItem("authProvider", "google");

            // Store profileType or role
            const userRole = profileType || role;
            if (userRole) {
                localStorage.setItem("userRole", userRole);
            }

            // Store hasLoggedInBefore flag
            if (hasLoggedInBefore !== null) {
                localStorage.setItem("hasLoggedInBefore", hasLoggedInBefore);
            }

            // Store hasProfile flag
            setHasProfile(hasProfile);

            // Store terms acceptance status
            setTermsAccepted(termsAccepted);

            // Store guardian athlete data if user is a guardian
            // Check both profileType from OAuth response and prepare for when it might be null
            const isGuardianUser = profileType === 'GUARDIAN' || role === 'GUARDIAN';
            if (isGuardianUser && guardianAthletesParam) {
                localStorage.setItem('guardianAthletes', guardianAthletesParam);
            }

            // Refresh AuthContext to load user info from backend
            // This will verify the session and update localStorage with backend-validated data
            refreshAuth().then(() => {
                const next = takePostLoginPath();
                const resolvedRole = loginRoleFromSources({
                    profileType,
                    role,
                    isGuardian: isGuardian() || isGuardianUser,
                    isPolaris: isPolaris(),
                    isAthlete: isAthlete(),
                });
                if (resolvedRole === "GUARDIAN") {
                    navigate(resolveGuardianPostLoginPath(next), { replace: true });
                    return;
                }
                const path = resolvePostLoginPath(resolvedRole, next, hasProfile);
                if (path === "/add-admin-profile") {
                    navigate(path, { replace: true, state: { firstTimeSetup: true } });
                    return;
                }
                navigate(path, { replace: true });
            }).catch((error) => {
                console.error("Failed to refresh auth after Google login:", error);
                // If refreshAuth fails, redirect to login
                navigate("/login?error=auth_refresh_failed", { replace: true });
            });
        } else {
            // If no userId, something went wrong
            navigate("/login?error=auth_failed", { replace: true });
        }
    }, [navigate, q, refreshAuth]);

    return (
        <main style={{ display: "grid", placeItems: "center", height: "100dvh" }}>
            <div className="flex flex-col items-center justify-center">
                <h2 className="text-2xl font-semibold mb-2">Authenticating...</h2>
                <p className="text-gray-600">Please wait while we complete your login.</p>
            </div>
        </main>
    );
}